import type { FC, ReactNode } from 'react'
import type { DrawingVariant } from '@/constants/content'

interface SectionDrawingProps {
    variant: DrawingVariant
}

interface SensorProps {
    x: number
    y: number
    zone?: 'core' | 'edge'
}

interface DefsProps {
    id: string
}

interface MemberProps {
    x: number
    y: number
    width: number
    height: number
    heat?: string
    active?: boolean
}

interface GroundProps {
    id: string
    d: string
}

interface LabelProps {
    x: number
    y: number
    children: ReactNode
    strong?: boolean
}

const Sensor: FC<SensorProps> = ({ x, y, zone = 'core' }): ReactNode => {
    if (zone === 'edge') {
        return <circle cx={x} cy={y} r='3.25' className='fill-card stroke-primary' strokeWidth='1.5' />
    }

    return <circle cx={x} cy={y} r='4' className='fill-primary stroke-card' strokeWidth='1.5' />
}

const Defs: FC<DefsProps> = ({ id }): ReactNode => {
    return (
        <defs>
            <pattern
                id={`hatch-${id}`}
                width='6'
                height='6'
                patternUnits='userSpaceOnUse'
                patternTransform='rotate(45)'
            >
                <line x1='0' y1='0' x2='0' y2='6' className='stroke-concrete-edge' strokeWidth='1' />
            </pattern>

            <radialGradient id={`heat-${id}`}>
                <stop offset='0%' className='[stop-color:var(--signal)] [stop-opacity:0.42]' />
                <stop offset='55%' className='[stop-color:var(--signal)] [stop-opacity:0.14]' />
                <stop offset='100%' className='[stop-color:var(--signal)] [stop-opacity:0]' />
            </radialGradient>
        </defs>
    )
}

const Member: FC<MemberProps> = ({
    x,
    y,
    width,
    height,
    heat,
    active = false
}): ReactNode => {
    return (
        <g>
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                rx='1.5'
                className={active ? 'fill-concrete stroke-primary' : 'fill-concrete stroke-concrete-edge'}
                strokeWidth={active ? 1.5 : 1}
            />

            {heat && (
                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    rx='1.5'
                    fill={`url(#heat-${heat})`}
                />
            )}
        </g>
    )
}

const Ground: FC<GroundProps> = ({ id, d }): ReactNode => {
    return (
        <path
            d={d}
            fill={`url(#hatch-${id})`}
            className='opacity-70'
        />
    )
}

const Label: FC<LabelProps> = ({
    x,
    y,
    children,
    strong = false
}): ReactNode => {
    return (
        <text
            x={x}
            y={y}
            textAnchor='middle'
            className={strong ? 'fill-primary font-mono text-[9px] font-semibold' : 'fill-muted-foreground font-mono text-[9px]'}
        >
            {children}
        </text>
    )
}

const Wanne: FC = (): ReactNode => {
    return (
        <>
            <Defs id='wanne' />
            <Ground id='wanne' d='M0 46H34V112H206V46H240V130H0Z' />
            <path d='M0 46H34M206 46H240' className='stroke-concrete-edge' strokeWidth='1' />
            <path d='M34 26H50V88H190V26H206V112H34Z' className='fill-concrete stroke-concrete-edge' strokeWidth='1' />
            <rect x='34' y='88' width='172' height='24' fill='url(#heat-wanne)' />
            <path d='M4 66q4-3 8 0t8 0t8 0M212 66q4-3 8 0t8 0t8 0' fill='none' className='stroke-chart-2' strokeWidth='1.25' />
            <Label x={18} y={60}>GW</Label>
            <Label x={222} y={60}>GW</Label>
            <Sensor x={120} y={101} />
            <Sensor x={120} y={92.5} zone='edge' />
            <Sensor x={42} y={62} />
        </>
    )
}

const Massiv: FC = (): ReactNode => {
    return (
        <>
            <Defs id='massiv' />
            <Ground id='massiv' d='M0 112H240V130H0Z' />
            <path d='M0 112H240' className='stroke-concrete-edge' strokeWidth='1' />
            <Member x={52} y={16} width={128} height={96} heat='massiv' />
            <Sensor x={116} y={64} />
            <Sensor x={116} y={24} zone='edge' />
            <Sensor x={60} y={64} zone='edge' />
            <Sensor x={116} y={104} zone='edge' />
            <path d='M194 16V112M190 16H198M190 112H198' fill='none' className='stroke-muted-foreground' strokeWidth='1' />
            <Label x={216} y={67}>1,20 m</Label>
        </>
    )
}

const Platte: FC = (): ReactNode => {
    return (
        <>
            <Defs id='platte' />
            <Ground id='platte' d='M0 94H240V130H0Z' />
            <rect x='8' y='86' width='224' height='8' className='fill-muted stroke-concrete-edge' strokeWidth='1' />
            <Member x={8} y={44} width={224} height={42} heat='platte' />
            <Sensor x={72} y={65} />
            <Sensor x={72} y={50.5} zone='edge' />
            <Sensor x={72} y={79.5} zone='edge' />
            <Sensor x={168} y={65} />
            <Sensor x={168} y={50.5} zone='edge' />
            <Sensor x={168} y={79.5} zone='edge' />
            <Label x={72} y={34}>MS 1</Label>
            <Label x={168} y={34}>MS 2</Label>
        </>
    )
}

const Tiefgarage: FC = (): ReactNode => {
    return (
        <>
            <Defs id='tiefgarage' />
            <Ground id='tiefgarage' d='M0 30H10V116H230V30H240V130H0Z' />
            <path d='M0 30H10M230 30H240' className='stroke-concrete-edge' strokeWidth='1' />
            <Member x={10} y={98} width={220} height={18} heat='tiefgarage' />
            <Member x={10} y={36} width={12} height={62} />
            <Member x={218} y={36} width={12} height={62} />
            <Member x={82} y={36} width={8} height={62} />
            <Member x={150} y={36} width={8} height={62} />
            <Member x={10} y={22} width={220} height={14} />
            <Sensor x={52} y={107} />
            <Sensor x={188} y={107} />
            <Sensor x={120} y={29} />
            <Sensor x={16} y={66} />
        </>
    )
}

const Abschnitte: FC = (): ReactNode => {
    return (
        <>
            <Defs id='abschnitte' />
            <Ground id='abschnitte' d='M0 96H240V130H0Z' />
            <path d='M0 96H240' className='stroke-concrete-edge' strokeWidth='1' />
            <Member x={10} y={58} width={70} height={38} />
            <Member x={85} y={58} width={70} height={38} />
            <Member x={160} y={58} width={70} height={38} heat='abschnitte' active />
            <Label x={45} y={46}>B1</Label>
            <Label x={120} y={46}>B2</Label>
            <Label x={195} y={46} strong>B3 · aktiv</Label>
            <Sensor x={45} y={77} />
            <Sensor x={120} y={77} />
            <Sensor x={195} y={77} />
            <Sensor x={195} y={64} zone='edge' />
        </>
    )
}

const DRAWINGS: Record<DrawingVariant, FC> = {
    wanne: Wanne,
    massiv: Massiv,
    platte: Platte,
    tiefgarage: Tiefgarage,
    abschnitte: Abschnitte
}

export const SectionDrawing: FC<SectionDrawingProps> = ({ variant }): ReactNode => {
    const Drawing = DRAWINGS[variant]

    return (
        <svg
            viewBox='0 0 240 130'
            aria-hidden='true'
            className='h-auto w-full'
        >
            <Drawing />
        </svg>
    )
}

export const DrawingLegend: FC = (): ReactNode => {
    return (
        <ul className='flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground'>
            <li className='inline-flex items-center gap-2'>
                <span className='size-2.5 rounded-full bg-primary' aria-hidden='true' />
                Sensor im Kern
            </li>

            <li className='inline-flex items-center gap-2'>
                <span className='size-2.5 rounded-full border-[1.5px] border-primary bg-card' aria-hidden='true' />
                Sensor in der Randzone
            </li>

            <li className='inline-flex items-center gap-2'>
                <span className='size-2.5 rounded-full bg-signal/40' aria-hidden='true' />
                Hydratationswärme
            </li>

            <li>Schematische Schnitte, nicht maßstäblich.</li>
        </ul>
    )
}
